"use client"

import { useState } from "react"
import { Trash2, Plus, Pencil, Check, X } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { useTagAliasesStore } from "@/lib/tag-aliases-store"
import { useTranslations } from "next-intl"

const cleanTag = (tag: string) => tag.trim().toLowerCase().replace(/\s+/g, "_")
const splitTags = (text: string) => text.split(",").map(cleanTag).filter(Boolean)

/** Alias groups: tags that mean the same thing (Settings > Tags > Aliases). The help line above the tabs explains what they do. */
export function TagAliasManager() {
  const tt = useTranslations("tagAliasManager")
  const { aliases, createAlias, deleteAlias, updateAlias } = useTagAliasesStore()
  const [name, setName] = useState("")
  const [tags, setTags] = useState("")
  const [error, setError] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTags, setEditingTags] = useState<string[]>([])
  const [editInput, setEditInput] = useState("")

  const handleCreate = () => {
    const list = Array.from(new Set(splitTags(tags)))
    if (!name.trim() || list.length < 2) {
      setError(true)
      return
    }
    createAlias(name.trim(), list)
    setName("")
    setTags("")
    setError(false)
  }

  const startEdit = (id: string, current: string[]) => {
    setEditingId(id)
    setEditingTags([...current])
    setEditInput("")
  }

  const addEditTags = () => {
    const next = Array.from(new Set([...editingTags, ...splitTags(editInput)]))
    setEditingTags(next)
    setEditInput("")
  }

  const saveEdit = (id: string) => {
    const next = Array.from(new Set([...editingTags, ...splitTags(editInput)]))
    if (next.length < 2) return
    updateAlias(id, next)
    setEditingId(null)
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Input
          id="alias-group-name"
          placeholder={tt("groupNameEGFeetVariations")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          className="h-8 text-sm"
        />
        <Input
          id="alias-group-tags"
          placeholder={tt("tagsSeparatedByCommasFeetPied")}
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          className="h-8 text-sm"
        />
        {error && <p className="text-xs text-destructive">{tt("needNameAndTwoTags")}</p>}
        <Button onClick={handleCreate} size="sm" className="h-8 w-full">
          <Plus className="h-4 w-4 mr-1" />
          {tt("createAliasGroup")}
        </Button>
      </div>

      {aliases.length > 0 && (
        <div className="space-y-2">
          {aliases.map((alias) => {
            const editing = editingId === alias.id
            return (
              <div key={alias.id} className="rounded-md border p-2 space-y-1.5">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium truncate flex-1">{alias.name}</span>
                  {editing ? (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label={tt("save")} onClick={() => saveEdit(alias.id)} disabled={editingTags.length < 2}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label={tt("cancel")} onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label={tt("edit")} onClick={() => startEdit(alias.id, alias.aliases)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" aria-label={tt("delete")} onClick={() => deleteAlias(alias.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-1">
                  {(editing ? editingTags : alias.aliases).map((tag) => (
                    <Badge key={tag} variant={editing ? "secondary" : "outline"} className="gap-1 text-xs">
                      {tag.replace(/_/g, " ")}
                      {editing && (
                        <button
                          type="button"
                          aria-label={tt("removeTag", { tag })}
                          onClick={() => setEditingTags(editingTags.filter((t) => t !== tag))}
                          className="opacity-70 hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>

                {editing && (
                  <div className="flex gap-2">
                    <Input
                      id={`alias-edit-${alias.id}`}
                      placeholder={tt("addTag")}
                      value={editInput}
                      onChange={(e) => setEditInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addEditTags()}
                      className="h-7 text-sm"
                    />
                    <Button size="sm" className="h-7 px-2" aria-label={tt("addTag")} onClick={addEditTags}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
